import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  ArrowLeft, Search, Sparkles, Users, ThumbsUp, ThumbsDown, Star, Building2,
  MapPin, DollarSign, Lightbulb, BarChart3, CheckCircle, ChevronRight, Filter,
  TrendingUp, Award, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

const STAGE_COLORS = {
  needs_mapping: '#6366F1', co_design: '#3B82F6', evaluation: '#8B5CF6',
  invitation: '#F59E0B', approval: '#10B981', launch: '#EF4444',
  monitoring: '#14B8A6', post_evaluation: '#F97316',
};

const fmtEUR = (n) => {
  if (n == null) return '—';
  try { return new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n); }
  catch { return `€${n}`; }
};

const StageBadge = ({ stage, title }) => (
  <Badge
    className="text-xs font-medium"
    style={{
      backgroundColor: (STAGE_COLORS[stage] || '#666') + '22',
      color: STAGE_COLORS[stage] || '#444',
      border: `1px solid ${(STAGE_COLORS[stage] || '#666')}55`,
    }}
    data-testid={`stage-badge-${stage}`}
  >
    {title || stage}
  </Badge>
);

const SummaryCard = ({ icon: Icon, label, value, tint = 'indigo' }) => (
  <Card data-testid={`summary-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-${tint}-50 text-${tint}-600`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const InvestorInsights = ({ user }) => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/investor-dashboard/idea-pipeline`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPipeline(res.data);
      } catch (e) {
        toast.error('Failed to load Investor Insights');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fetch the full idea detail (read-only) when an idea is selected
  useEffect(() => {
    if (!selectedIdea) { setSelectedDetail(null); return; }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/participatory-design/ideas/${selectedIdea.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSelectedDetail(res.data);
      } catch {
        setSelectedDetail(null);
      }
    })();
  }, [selectedIdea]);

  const stagesList = useMemo(() => {
    if (!pipeline) return [];
    return Object.keys(pipeline.summary.by_stage || {});
  }, [pipeline]);

  const actorsList = useMemo(() => {
    if (!pipeline) return [];
    const set = new Map();
    pipeline.ideas.forEach((i) => {
      if (i.social_actor_id) set.set(i.social_actor_id, i.social_actor_name || 'Unknown');
    });
    return Array.from(set.entries()).map(([id, name]) => ({ id, name }));
  }, [pipeline]);

  const filtered = useMemo(() => {
    if (!pipeline) return [];
    return pipeline.ideas.filter((i) => {
      if (stageFilter !== 'all' && i.stage !== stageFilter) return false;
      if (actorFilter !== 'all' && i.social_actor_id !== actorFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(i.title || '').toLowerCase().includes(s)
          && !(i.description || '').toLowerCase().includes(s)
          && !(i.social_actor_name || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [pipeline, stageFilter, actorFilter, search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!pipeline) {
    return <div className="p-8 text-gray-500">Insights unavailable.</div>;
  }

  const s = pipeline.summary;

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-6" data-testid="investor-insights-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="h-7 w-7 text-indigo-600" /> Investor Insights
              </h1>
              <p className="text-sm text-gray-500">
                Read-only due-diligence view of the Participatory-Design idea pipeline.
                Browse, compare and identify opportunities you may want to back.
              </p>
            </div>
          </div>
          <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-xs">
            Read-only · {user?.organization_name || user?.username}
          </Badge>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="insights-summary">
          <SummaryCard icon={Lightbulb} label="Pipeline ideas" value={s.total_ideas} tint="indigo" />
          <SummaryCard icon={DollarSign} label="Total budget" value={fmtEUR(s.total_pipeline_budget_eur)} tint="emerald" />
          <SummaryCard icon={CheckCircle} label="Promoted to SI" value={s.transferred_to_investment} tint="amber" />
          <SummaryCard icon={Star} label="Avg evaluation" value={s.avg_evaluation_score ? `${s.avg_evaluation_score} / 5` : '—'} tint="rose" />
          <SummaryCard icon={TrendingUp} label="Post-Evaluation ready" value={s.by_stage?.post_evaluation || 0} tint="orange" />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search by title, description or social actor…"
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="insights-search"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500"><Filter className="h-3.5 w-3.5" /> Stage</div>
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-44" data-testid="insights-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  {stagesList.map((st) => (
                    <SelectItem key={st} value={st}>{st.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={actorFilter} onValueChange={setActorFilter}>
                <SelectTrigger className="w-52" data-testid="insights-actor-filter"><SelectValue placeholder="Social Actor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All organisations</SelectItem>
                  {actorsList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Ideas list */}
          <div className="lg:col-span-3 space-y-3" data-testid="insights-ideas-list">
            {filtered.length === 0 ? (
              <Card><CardContent className="p-10 text-center text-sm text-gray-500">No ideas match your filters.</CardContent></Card>
            ) : filtered.map((idea) => {
              const isSelected = selectedIdea?.id === idea.id;
              return (
                <Card
                  key={idea.id}
                  onClick={() => setSelectedIdea(idea)}
                  className={`cursor-pointer transition-shadow hover:shadow-md ${isSelected ? 'ring-2 ring-indigo-400' : ''}`}
                  data-testid={`insights-idea-${idea.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 truncate">{idea.title}</h3>
                          <StageBadge stage={idea.stage} title={idea.stage_title} />
                          {idea.transferred_to_investment_id && (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" /> Promoted
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2 mt-1">{idea.description}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                          {idea.social_actor_name && (
                            <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{idea.social_actor_name}</span>
                          )}
                          {idea.location && (
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{idea.location}</span>
                          )}
                          {idea.budget != null && (
                            <span className="inline-flex items-center gap-1"><DollarSign className="h-3 w-3" />{fmtEUR(idea.budget)}</span>
                          )}
                          <span className="inline-flex items-center gap-1 text-emerald-700"><ThumbsUp className="h-3 w-3" />{idea.upvotes}</span>
                          <span className="inline-flex items-center gap-1 text-rose-600"><ThumbsDown className="h-3 w-3" />{idea.downvotes}</span>
                          {idea.evaluation_count > 0 && (
                            <span className="inline-flex items-center gap-1 text-amber-600">
                              <Star className="h-3 w-3 fill-amber-500 text-amber-700" />
                              {idea.avg_evaluation_score} ({idea.evaluation_count})
                            </span>
                          )}
                          {idea.workshop_count > 0 && (
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{idea.workshop_count} workshops</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Detail side panel */}
          <div className="lg:col-span-2 lg:sticky lg:top-4 self-start" data-testid="insights-detail-panel">
            {!selectedIdea ? (
              <Card>
                <CardContent className="p-8 text-center text-sm text-gray-500">
                  <Lightbulb className="h-10 w-10 mx-auto text-gray-200 mb-2" />
                  Select an idea on the left to view its read-only insights.
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-start justify-between gap-2">
                    <span className="truncate">{selectedIdea.title}</span>
                    <StageBadge stage={selectedIdea.stage} title={selectedIdea.stage_title} />
                  </CardTitle>
                  {selectedIdea.social_actor_name && (
                    <p className="text-xs text-gray-500 inline-flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> {selectedIdea.social_actor_name}
                      {selectedIdea.actor?.country && <span className="text-gray-500">· {selectedIdea.actor.country}</span>}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedIdea.description}</p>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs uppercase text-gray-500">Budget</p>
                      <p className="font-semibold text-gray-800">{fmtEUR(selectedIdea.budget)}</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs uppercase text-gray-500">Vote balance</p>
                      <p className="font-semibold text-gray-800">{selectedIdea.vote_balance >= 0 ? `+${selectedIdea.vote_balance}` : selectedIdea.vote_balance}</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs uppercase text-gray-500">Avg evaluation</p>
                      <p className="font-semibold text-gray-800">{selectedIdea.avg_evaluation_score ? `${selectedIdea.avg_evaluation_score} / 5` : '—'} <span className="text-gray-500 font-normal">({selectedIdea.evaluation_count} reviews)</span></p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs uppercase text-gray-500">Stakeholders</p>
                      <p className="font-semibold text-gray-800">{selectedIdea.stakeholder_count}</p>
                    </div>
                  </div>

                  {selectedIdea.transferred_to_investment_id && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center justify-between gap-2" data-testid="insights-promoted-banner">
                      <span className="inline-flex items-center gap-1.5">
                        <CheckCircle className="h-3.5 w-3.5" /> Promoted to Social Investment Registry
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 h-7 text-xs"
                        onClick={() => navigate(`/social-investment-registration?open=${selectedIdea.transferred_to_investment_id}`)}
                        data-testid="insights-open-investment-btn"
                      >
                        Open <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Button>
                    </div>
                  )}

                  {selectedIdea.actor && (
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <p className="text-xs uppercase text-gray-500 mb-1">Proposing organisation</p>
                      <p className="font-semibold text-sm text-gray-800">{selectedIdea.actor.name}</p>
                      <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                        {selectedIdea.actor.sector && <span>{selectedIdea.actor.sector}</span>}
                        {selectedIdea.actor.certification_level && (
                          <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-xs">
                            <Award className="h-2.5 w-2.5 mr-0.5" /> {String(selectedIdea.actor.certification_level).replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>
                      {(selectedIdea.actor.sdg_alignment || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedIdea.actor.sdg_alignment.map((s) => (
                            <Badge key={s} className="bg-green-50 text-green-700 border-green-100 text-xs">SDG {s}</Badge>
                          ))}
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="link"
                        className="text-xs text-indigo-600 px-0 h-auto mt-1"
                        onClick={() => navigate(`/social-actor-registry?focus=${selectedIdea.actor.id}`)}
                        data-testid="insights-open-actor-btn"
                      >
                        View Social Actor profile <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Button>
                    </div>
                  )}

                  {/* Extra tabs from full idea load */}
                  {selectedDetail && (
                    <Tabs defaultValue="evaluations">
                      <TabsList className="grid grid-cols-3 h-8">
                        <TabsTrigger value="evaluations" className="text-xs">Evaluations</TabsTrigger>
                        <TabsTrigger value="stakeholders" className="text-xs">Stakeholders</TabsTrigger>
                        <TabsTrigger value="history" className="text-xs">Timeline</TabsTrigger>
                      </TabsList>
                      <TabsContent value="evaluations" className="pt-2 space-y-1.5">
                        {(selectedDetail.evaluations || []).length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-4">No evaluations yet.</p>
                        ) : selectedDetail.evaluations.map((ev) => (
                          <div key={ev.id} className="rounded border border-gray-100 bg-white p-2 text-xs">
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`h-3 w-3 ${i < ev.score ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
                              ))}
                              <span className="text-xs text-gray-500 ml-1">by {ev.evaluator_name}</span>
                            </div>
                            {ev.feedback && <p className="text-gray-700 mt-1">{ev.feedback}</p>}
                          </div>
                        ))}
                      </TabsContent>
                      <TabsContent value="stakeholders" className="pt-2 space-y-1">
                        {(selectedDetail.stakeholders || []).length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-4">No stakeholders mapped.</p>
                        ) : selectedDetail.stakeholders.map((sh) => (
                          <div key={sh.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                            <span className="font-medium text-gray-800">{sh.name}</span>
                            <span className="text-xs text-gray-500">Inf {sh.influence} · Int {sh.interest}</span>
                          </div>
                        ))}
                      </TabsContent>
                      <TabsContent value="history" className="pt-2 space-y-1">
                        {(selectedDetail.stage_history || []).length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-4">No stage transitions recorded.</p>
                        ) : selectedDetail.stage_history.map((h, idx) => (
                          <div key={idx} className="text-xs flex items-center gap-2">
                            <ChevronRight className="h-3 w-3 text-gray-300" />
                            <span className="text-gray-500">{h.from_stage} → <span className="font-medium text-gray-800">{h.to_stage}</span></span>
                            <span className="text-xs text-gray-500 ml-auto">{new Date(h.changed_at).toLocaleDateString('en-GB')}</span>
                          </div>
                        ))}
                      </TabsContent>
                    </Tabs>
                  )}

                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => navigate('/participatory-planning')}
                      data-testid="insights-open-in-pp-btn"
                    >
                      Open in Participatory Design (view-only) <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center pt-4">
          Insights are read-only. Investors cannot create or edit Participatory-Design ideas.
        </p>
      </div>
    </div>
  );
};

export default InvestorInsights;
